// src/portal/PortalUploadPanel.jsx
//
// The submitter's upload: inspect, pick a layer, preview, tag, submit.
//
// ── EVERY ACTION A REFUSAL CAN NAME HAS A CONTROL, IN EVERY STATE THE REFUSAL
//    CAN LAND IN ─────────────────────────────────────────────────────────────
//
// portalRefusalCopy.js names one of four actions on a refusal. Three are
// reachable by construction and one is not:
//
//   contact          text, always present
//   reload           the browser's, always available
//   restart_upload   rendered in every state past `idle` — see RESTART below
//   choose_file      THE ONE THAT CAN BREAK
//
// `choose_file` breaks in the obvious implementation, which swaps the file
// input out for the layer list once a file has been inspected. Route 4 re-runs
// prepareFeatures (§3.15), so `unsupported_geometry` and `geom_invalid` are
// emittable from SUBMIT — landing the submitter in `tagging`, being told to pick
// a different file, with no way to pick one.
//
// So THE FILE INPUT IS MOUNTED IN EVERY SETTLED STATE, not only in `idle`. It is
// labelled for what it does in that state ("Choose a plan file" vs "Choose a
// different file") but it is the same control and it is always there.
// PortalUploadPanel.reachability.test.jsx renders every (state, code) pair and
// requires the named action's control to be in the document.
//
// ── THE RETRY BUTTON IS ABSENT WHEN retry IS "no" ──────────────────────────
//
// Not disabled — absent. A disabled button still advertises the action, and the
// whole point of `retry: "no"` is that the identical request fails identically:
// nginx's HTML 413 on a 60 MB file is the measured case (§3.16), where taking
// the offer costs minutes of upload to arrive back here.

import { useRef, useState } from "react";
import { inspectUpload, previewSublayer, submitRevision } from "../api/portalApi";
import { dispositionFor } from "../api/portalRefusals";
import { copyFor } from "../api/portalRefusalCopy";
import { advance, retreat } from "./portalUploadMachine";
import {
  emptyTags, setTag, clearTag, tagOf, tagsForWire, tagProgress, SUBMITTER_TAGS,
} from "./portalTags";
import PortalSubmitterMap from "./PortalSubmitterMap";

const BUSY = { inspecting: "Reading the file…", previewing: "Converting the layer…", submitting: "Submitting…" };

// UI COPY, keyed by the server's vocabulary. Not a second declaration of the
// vocabulary -- the buttons are generated from SUBMITTER_TAGS and this only
// supplies text, with the tag itself as the fallback. A test asserts every
// member of SUBMITTER_TAGS has an entry, so a fourth tag added server-side
// surfaces as a failing assertion rather than as a button labelled "widget".
const TAG_LABEL = { pad: "Pad", road: "Access road", other: "Other" };

export default function PortalUploadPanel({ planReviewId, onSubmitted, initialState = "idle" }) {
  const [state, setState] = useState(initialState);
  const [failure, setFailure] = useState(null);   // {copy, disposition}
  const [sublayers, setSublayers] = useState([]);
  const [features, setFeatures] = useState([]);
  // A Map, not an object: untagged is ABSENCE from it, so there is no fourth
  // value for anything to leak (§3.20).
  const [tags, setTags] = useState(emptyTags);
  const [selected, setSelected] = useState(null);
  const token = useRef(null);
  const chosen = useRef(null);      // the sublayer index
  const lastFile = useRef(null);

  const busy = Boolean(BUSY[state]);

  function fail(err, from) {
    const disposition = dispositionFor(err);
    setFailure({ copy: copyFor(err, disposition), disposition });
    setState(retreat(from));
  }

  async function runInspect(file) {
    lastFile.current = file;
    setFailure(null);
    setState("inspecting");
    try {
      const r = await inspectUpload(planReviewId, file);
      token.current = r.upload_token;
      setSublayers(r.sublayers ?? []);
      setState(advance("inspecting"));
    } catch (e) { fail(e, "inspecting"); }
  }

  async function runPreview(index) {
    chosen.current = index;
    setFailure(null);
    setState("previewing");
    try {
      const r = await previewSublayer(planReviewId, { uploadToken: token.current, sublayerIndex: index });
      setFeatures(r.features ?? []);
      setTags(emptyTags());
      setSelected(null);
      setState(advance("previewing"));
    } catch (e) { fail(e, "previewing"); }
  }

  async function runSubmit() {
    setFailure(null);
    setState("submitting");
    try {
      await submitRevision(planReviewId, {
        uploadToken: token.current, sublayerIndex: chosen.current,
        // tagsForWire is the ONLY producer of this payload. It iterates the
        // FEATURES, so every ordinal is route 3's own -- a Map key is never the
        // source of one, which is what makes an off-by-one impossible here
        // rather than merely unlikely.
        tags: tagsForWire(tags, features),
      });
      setState("submitted");
      onSubmitted?.();
    } catch (e) { fail(e, "submitting"); }
  }

  /** The action the last failure asked for, or null. */
  const action = failure?.copy?.action ?? null;
  const retry = failure?.copy?.retry ?? null;

  /** Re-run whatever call the submitter was making. Only offered when retry !== "no". */
  function retryLast() {
    if (state === "idle" && lastFile.current) return runInspect(lastFile.current);
    if (state === "layers") return runPreview(chosen.current ?? 0);
    if (state === "tagging") return runSubmit();
  }

  return (
    <section className="flex flex-col gap-3" data-testid="upload-panel" data-state={state}>
      <h3 className="text-sm font-medium">Submit a plan</h3>

      {failure && (
        <div role="alert" className="rounded border border-bg-border bg-bg-soft px-3 py-2">
          <p className="text-sm font-medium">{failure.copy.title}</p>
          {failure.copy.detail && (
            <p className="mt-1 text-xs text-fg-secondary">{failure.copy.detail}</p>
          )}

          {/* `contact` has no button — there is no reference number to quote
              (REM-380), so the time is the actionable part and it is rendered
              as a real timestamp rather than described in prose. */}
          {/* `contact` has no BUTTON — there is no reference number to quote
              (REM-380), so the time is the actionable part. It still carries an
              `action-contact` testid, so the reachability sweep can require a
              control for EVERY action uniformly rather than carrying an
              exception for this one. An exception in the checker is where a
              genuinely missing control would hide. */}
          {action === "contact" && (
            <p className="mt-1 text-xs text-fg-secondary" data-testid="action-contact">
              Please mention this time:{" "}
              <time data-testid="failure-time" dateTime={new Date().toISOString()}>
                {new Date().toLocaleString()}
              </time>
            </p>
          )}

          <div className="mt-2 flex flex-wrap gap-2">
            {retry !== "no" && !busy && (
              <button type="button" onClick={retryLast} data-testid="action-retry"
                className="rounded border border-bg-border px-2 py-1 text-xs">
                Try again
              </button>
            )}
            {action === "reload" && (
              <button type="button" onClick={() => window.location.reload()} data-testid="action-reload"
                className="rounded border border-bg-border px-2 py-1 text-xs">
                Reload the page
              </button>
            )}
          </div>
        </div>
      )}

      {busy && <p className="text-xs text-fg-secondary" data-testid="busy">{BUSY[state]}</p>}

      {/* ── THE FILE INPUT. MOUNTED IN EVERY SETTLED STATE. ──────────────────
          Not only in `idle`: a refusal from submit lands in `tagging` and can
          name choose_file, and an input that is not there is a dead end. */}
      {!busy && state !== "submitted" && (
        <label className="flex flex-col gap-1 text-xs">
          <span>{state === "idle" ? "Choose a plan file" : "Choose a different file"}</span>
          <input
            type="file"
            data-testid="action-choose_file"
            accept=".geojson,.json,.kml,.kmz,.zip"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) runInspect(f); }}
          />
        </label>
      )}

      {/* ── RESTART. Present in every state past idle, which is what makes
          restart_upload reachable by construction. */}
      {!busy && state !== "idle" && state !== "submitted" && (
        <button
          type="button"
          data-testid="action-restart_upload"
          onClick={() => {
            token.current = null; chosen.current = null;
            setSublayers([]); setFeatures([]); setTags(emptyTags()); setSelected(null);
            setFailure(null); setState("idle");
          }}
          className="self-start rounded border border-bg-border px-2 py-1 text-xs"
        >
          Start the upload again
        </button>
      )}

      {/* ── THE LAYER LIST. `usable` AND `refusal` ARE RENDERED. ─────────────
          Route 2 lets a bad layer TRAVEL rather than sinking the upload, with a
          stated reason: a multi-layer file may carry one layer with a
          projection and one without, and refusing the whole upload would make
          the submitter guess which. This list dropped both fields and offered
          every layer as an identical enabled button — so the submitter picked
          the broken one, waited for a conversion, and was told `crs_undeclared`
          by a response that had already said so before they clicked.

          Disabled, not enabled-with-a-warning: an enabled control that the
          server has already committed to refusing is the round-trip we are
          removing, not a choice.

          `refusal.error` is shown VERBATIM. It is a coded refusal, so §3.18's
          rule applies — the server authored it for a submitter to read. */}
      {state === "layers" && (
        <div className="flex flex-col gap-2">
          {sublayers.length > 0 && sublayers.every((s) => s.usable === false) && (
            <p className="text-xs text-fg-secondary" data-testid="no-usable-layers">
              None of the layers in this file can be used. Each one says why below.
            </p>
          )}
          <ul className="flex flex-col gap-1" data-testid="sublayers">
            {sublayers.map((s) => {
              // `usable === false`, not `!s.usable`. A response that predates
              // the field, or one that omits it, must not silently disable
              // every layer — the absence of a refusal is not a refusal.
              const blocked = s.usable === false;
              return (
                <li key={s.index} className="flex flex-col gap-0.5">
                  <button
                    type="button"
                    disabled={blocked}
                    data-testid={`sublayer-${s.index}`}
                    onClick={() => runPreview(s.index)}
                    className="self-start rounded border border-bg-border px-2 py-1 text-xs disabled:opacity-50"
                  >
                    {s.name} ({s.feature_count})
                  </button>
                  {blocked && (
                    <p className="pl-1 text-[11px] text-fg-secondary"
                       data-testid={`sublayer-refusal-${s.index}`}>
                      {s.refusal?.error ?? "This layer cannot be used."}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* ── THE PLAN, ON A MAP ───────────────────────────────────────────────
          Decision 8 locked "the submitter sees parsed features ON A MAP and
          tags each" -- the design doc dropped "on a map" in transcription
          (§3.19's audit), and what shipped was the sentence "N shapes to tag"
          with no geometry anywhere. Tagging is the submitter's ASSERTION ABOUT
          THEIR OWN GEOMETRY, and an assertion made blind is not one.

          Every feature here is UNTAGGED, which now has a rendering of its own:
          no fill, dashed edge, its own colour. Until this commit untagged and
          `other` were the same grey, so a map of untagged shapes would have
          looked like a map on which everything had already been tagged
          `other`.

          The tag control is the next commit. This one is not inert: the
          submitter can see what they uploaded, which they could not before. */}
      {state === "tagging" && (() => {
        const progress = tagProgress(tags, features);
        const complete = progress.tagged === progress.total && progress.total > 0;
        const selectedFeature = features.find((f) => f.ordinal === selected) ?? null;
        return (
          <div className="flex flex-col gap-2" data-testid="tagging">
            <div className="flex gap-3">
              <div className="h-[24rem] min-w-0 flex-1 overflow-hidden rounded border border-bg-border"
                   data-testid="tagging-map">
                <PortalSubmitterMap
                  features={features}
                  selectedOrdinal={selected}
                  onSelectFeature={setSelected}
                />
              </div>

              {/* ── THE LIST. ALWAYS PRESENT, AND IT IS WAYFINDING. ──────────
                  At two features it is two rows and costs nothing; it is the
                  keyboard-reachable path to every shape (the map is mouse-only,
                  §3.19's stated limit), and it is the only way to see WHICH
                  shapes are still untagged rather than just how many.
                  ONE SELECTION, TWO VIEWS — a row and a shape are the same
                  selection, not two controls. */}
              <ul className="h-[24rem] w-56 shrink-0 overflow-auto rounded border border-bg-border p-1"
                  data-testid="feature-list">
                {features.map((f) => {
                  const tag = tagOf(tags, f.ordinal);
                  return (
                    <li key={f.ordinal}>
                      <button
                        type="button"
                        data-testid={`feature-row-${f.ordinal}`}
                        aria-pressed={selected === f.ordinal}
                        onClick={() => setSelected(f.ordinal)}
                        className={`w-full rounded px-2 py-1 text-left text-xs ${
                          selected === f.ordinal ? "bg-bg-soft" : ""}`}
                      >
                        <span className="block truncate">{f.label ?? `Shape ${f.ordinal}`}</span>
                        <span className="text-[11px] text-fg-secondary">
                          {f.geom_class}
                          {" · "}
                          <span data-testid={`feature-tag-${f.ordinal}`}>{tag ?? "untagged"}</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* ── THE TAG CONTROL. SELECT, THEN ASSERT. ─────────────────────
                Two deliberate acts rather than one: in a paint mode a mis-click
                WRITES a wrong assertion silently, and a wrong `pad` on a road is
                a false statement in a record the reviewer relies on and the
                export carries. Here a mis-click only changes what is selected.

                Buttons are generated from SUBMITTER_TAGS, imported from the
                server, so a fourth tag cannot be added by typing one. */}
            <div className="flex flex-wrap items-center gap-2" data-testid="tag-control">
              <span className="text-xs text-fg-secondary">
                {selectedFeature
                  ? `${selectedFeature.label ?? `Shape ${selectedFeature.ordinal}`}:`
                  : "Select a shape to tag it."}
              </span>
              {SUBMITTER_TAGS.map((t) => (
                <button
                  key={t}
                  type="button"
                  disabled={!selectedFeature}
                  aria-pressed={selectedFeature ? tagOf(tags, selectedFeature.ordinal) === t : false}
                  data-testid={`tag-${t}`}
                  onClick={() => setTags((cur) => setTag(cur, selectedFeature.ordinal, t))}
                  className="rounded border border-bg-border px-2 py-1 text-xs disabled:opacity-50"
                >
                  {TAG_LABEL[t] ?? t}
                </button>
              ))}
              {/* Withdrawing an assertion is not the same as making a different
                  one. A submitter who is unsure would otherwise reach for
                  `other`, which is a false claim rather than an absent one. */}
              <button
                type="button"
                disabled={!selectedFeature || tagOf(tags, selectedFeature.ordinal) === null}
                data-testid="tag-clear"
                onClick={() => setTags((cur) => clearTag(cur, selectedFeature.ordinal))}
                className="rounded border border-bg-border px-2 py-1 text-xs disabled:opacity-50"
              >
                Clear
              </button>
            </div>

            <p className="text-xs text-fg-secondary" data-testid="tag-progress">
              {progress.tagged} of {progress.total} tagged.
            </p>

            <button
              type="button"
              onClick={runSubmit}
              disabled={!complete}
              data-testid="submit-plan"
              className="self-start rounded border border-bg-border px-2 py-1 text-xs disabled:opacity-50"
            >
              Submit the plan
            </button>
          </div>
        );
      })()}

      {state === "submitted" && <p className="text-sm" data-testid="submitted">Plan submitted.</p>}
    </section>
  );
}
